import {ArrowRightIcon} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import React, {useMemo, useRef} from 'react';

import {MoneyIcon} from '../../../resources/Icons';
import Col from '../../common/Col';
import Row from '../../common/Row';
import {useBreakpoint} from '../../common/util/useBreakpoint';
import {formatMoney} from '../mechanics';
import InfoPill from './buttons/InfoPill';
import GameAudio, {GameAudioHandle} from './GameAudio';
import MessageBox from './MessageBox';

type Props = {
  className?: string;
  title?: string;
  description?: string;
  buttonMessage?: string;
  icon?: React.ReactNode;
  selected?: boolean;
  cost?: number;
  costPerHa?: number;
  canPay?: boolean;
  onClick?: () => void;
  onConfirm: () => void;
  style?: React.CSSProperties;
};

const Choice = (props: Props) => {
  const {
    className,
    title,
    description,
    buttonMessage = 'Confirm Answer',
    icon,
    selected,
    cost,
    costPerHa,
    canPay = true,
    onConfirm,
    onClick,
    style,
  } = props;

  const audioRef = useRef<GameAudioHandle>(null);
  const {isMobile} = useBreakpoint('mobile');

  const costPerHaLabel = !isMobile && costPerHa ? `${formatMoney(-costPerHa)}/ha` : undefined;

  const Content = useMemo(
    () => (
      <MessageBox
        style={{flexGrow: 1}}
        className={classNames(
          'text-center min-h-[20rem] mobile:min-h-[16rem]',
          selected && '!bg-primary-100 cursor-auto',
          !selected && canPay && 'hover:shadow-solid-green hover:border-primary-300 cursor-pointer'
        )}
        bottomButton={
          <Row className="items-center">
            <Col className="grow ml-sm text-xl mobile:font-medium mobile:text-base">{buttonMessage}</Col>
            <Col>
              <ArrowRightIcon className="sm w-sm" />
            </Col>
          </Row>
        }
        bottomButtonInvisible={!selected || !canPay}
        alertText={!canPay ? 'You do not have enough money' : undefined}
        onClickBottomButton={onConfirm}
        onMouseDownBottomButton={() => audioRef.current?.play()}
      >
        <GameAudio ref={audioRef} src="sounds/click.mp3" />
        <Col className={classNames('h-full space-y-4 justify-center mt-sm', className)}>
          {icon && <div>{icon}</div>}
          <div className="text-3xl font-medium mobile:font-semibold leading-tight mobile:text-2xl">{title}</div>
          <div className="text-xl font-medium mobile:font-semibold leading-tight mobile:text-lg">{description}</div>
          {cost !== undefined && (
            <>
              <span className={'sr-only'}>{`Cost: ${cost} £`}</span>
              {costPerHaLabel && <span className={'sr-only'}>{`Cost per hectare: ${costPerHa} £`}</span>}
              <InfoPill
                className={'!my-xs mx-auto w-fit'}
                icon={<MoneyIcon className="h-[45px] w-[45px]" />}
                label={costPerHaLabel}
                sublabel={formatMoney(-cost)}
                rightAlignSublabel
                reversed
                danger={cost > 0}
                aria-hidden
              />
            </>
          )}
        </Col>
      </MessageBox>
    ),
    [buttonMessage, canPay, className, cost, costPerHa, costPerHaLabel, description, icon, onConfirm, selected, title]
  );

  return selected ? (
    <div className={'flex flex-col items-stretch p-[2px]'} style={style}>
      {Content}
    </div>
  ) : (
    <button className={'flex flex-col items-stretch p-[2px]'} style={style} onClick={canPay ? onClick : undefined}>
      {Content}
    </button>
  );
};

export default Choice;
